if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }

        // Make sure hidden id isn't disabled at init
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        // Bind
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.updateVariantId = this.updateVariantId.bind(this);

        // Events
        this.form.addEventListener('submit', this.onSubmitHandler);

        // Only listen within this form
        this.form.addEventListener('change', (e) => {
          const n = e.target?.getAttribute?.('name') || '';
          if (/^options\[/i.test(n)) this.updateVariantId();
        });

        // First sync
        this.updateVariantId();
      }

      _readOption(optionName) {
        const radios = this.form.querySelectorAll(
          `input[type="radio"][name="options[${CSS.escape(optionName)}]"]`
        );
        if (radios.length) {
          const r = Array.from(radios).find((x) => x.checked);
          return r ? r.value : '';
        }
        const sel = this.form.querySelector(
          `select[name="options[${CSS.escape(optionName)}]"]`
        );
        return sel ? sel.value : '';
      }

      updateVariantId() {
        const variantsEl = this.form.querySelector('[data-variants-json]');
        if (!variantsEl) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsEl.textContent || '[]');
        } catch (e) {
          console.error('Error parsing variants JSON', e);
        }

        // Collect option names present in the form (order matters)
        const optionInputs = this.form.querySelectorAll('[name^="options["]');
        const names = [...new Set([...optionInputs].map((i) => i.name))];

        // Current selections
        const selected = names.map((name) => {
          const radios = this.form.querySelectorAll(
            `input[type="radio"][name="${CSS.escape(name)}"]`
          );
          if (radios.length) {
            const checked = Array.from(radios).find((r) => r.checked);
            return checked ? checked.value : '';
          }
          const el = this.form.querySelector(`[name="${CSS.escape(name)}"]`);
          return el ? el.value : '';
        });

        // Match variant by options array
        const match = variants.find(
          (v) =>
            v.options?.length === selected.length &&
            v.options.every((opt, i) => opt === selected[i])
        );

        // Determine mode (option1 = AVAILABILITY)
        const availValue =
          this._readOption('AVAILABILITY') || this._readOption('Availability');
        const isPreorder = availValue === 'PRE-ORDER';

        if (match) {
          // Keep hidden id in sync
          if (this.variantIdInput) this.variantIdInput.value = String(match.id);

          // Lite gating (back to your previous behavior):
          // PRE-ORDER: allow
          // ON HAND: rely on Shopify's match.available (as before)
          const canSell = isPreorder ? true : !!match.available;

          if (canSell) {
            this.variantIdInput?.removeAttribute('disabled');
            this.submitButton?.removeAttribute('disabled');
            this.submitButton?.setAttribute('aria-disabled', 'false');
            if (this.submitButtonText) {
              this.submitButtonText.textContent = isPreorder
                ? 'Pre-order'
                : (window.variantStrings?.addToCart || 'Add to cart');
            }
          } else {
            // Don’t hide payment buttons; just reflect UI state
            this.variantIdInput?.setAttribute('disabled', 'disabled');
            this.submitButton?.setAttribute('disabled', 'disabled');
            this.submitButton?.setAttribute('aria-disabled', 'true');
            if (this.submitButtonText) {
              this.submitButtonText.textContent =
                window.variantStrings?.unavailable || 'Sold out';
            }
          }
        } else {
          // No valid variant -> show unavailable
          this.variantIdInput?.setAttribute('disabled', 'disabled');
          this.submitButton?.setAttribute('disabled', 'disabled');
          this.submitButton?.setAttribute('aria-disabled', 'true');
          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings?.unavailable || 'Unavailable';
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Only guard if no variant id is present
        if (!this.variantIdInput || !this.variantIdInput.value) {
          this.handleErrorMessage('Please select a valid variant.');
          return;
        }

        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton?.setAttribute('aria-disabled', true);
        this.submitButton?.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton?.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton?.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton?.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText)
            this.submitButtonText.textContent = text;
        } else {
          this.submitButton?.removeAttribute('disabled');
          if (this.submitButtonText)
            this.submitButtonText.textContent =
              window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
